/**
 * The farm's sensor devices and the pen each one hangs in.
 *
 * Mirrors `GET /farms/{farmId}/sensor-devices` (swagger:
 * `HealthSystem.API.Controllers.SensorDevices.Responses.SensorDeviceResponse`). Hand-written rather than
 * re-aliased from the generated client, which declares every nullable field as `?: T | null` - the service
 * normalises those to a plain `T | null` so no consumer has to distinguish "absent" from "null" when the API
 * only ever means one thing by it.
 *
 * The shape to keep in mind: a device is discovered at the provider, which knows the customer's organization
 * and a free-text name the farmer typed there - and nothing about rooms or pens. So the backend binds a newly
 * discovered device to an **arbitrary** free pen, and the binding is a guess until somebody who has walked the
 * barn corrects it. That is what this whole screen exists for.
 */
export interface SensorDevice {
  id: string;
  /** The service the device was discovered through. */
  provider: SensorDeviceProvider;
  /** The device's id at the provider. Stable, but meaningless to a farmer. */
  externalId: string;
  /**
   * The device name as it reads at the provider - free text the farmer typed there, and usually the only
   * clue to which barn the device is in. Show it prominently; it is what the reader matches against reality.
   */
  externalName: string;
  /** The provider's source tag: `kpn-lora` and `ttn` are climate boxes, `visual-crossing` is a web service. */
  externalDomain: string;
  deviceType: SensorDeviceType;
  /**
   * What the device measures, in the provider's own vocabulary. The backend stores these without validating
   * them, so they are rendered verbatim - translating them would invent meaning the API never promised.
   */
  measuredTags: string[];
  /** Null when the device is bound to no pen. Normal, not an error - it just stores nothing until it is. */
  locationId: string | null;
  locationNumber: string | null;
  locationRoom: string | null;
  locationPen: string | null;
  /** When the provider last reported the device. Null when it has never been seen. */
  lastSeenAt: string | null;
  /**
   * The provider has stopped listing this device - typically because it was never this farm's to begin with,
   * and a later sync no longer found it under their organization.
   *
   * A retired device holds no location and receives nothing further. It is kept rather than deleted so the
   * readings it did take stay readable, and so there is an answer to "why did this pen go quiet".
   */
  isRetired: boolean;
  /** When it was retired. Null while the provider still reports it. */
  retiredAt: string | null;
}

/**
 * `POST /farms/{farmId}/sensor-devices/discover`.
 *
 * Registers and binds devices only - it imports **no readings**. A two-year backfill has no business running
 * inside a request, so the readings still arrive with the daily worker. A device can therefore show up in the
 * table immediately while its climate charts stay empty.
 */
export interface DiscoverSensorDevicesResult {
  devicesFound: number;
  /** How many of them were not already known. */
  devicesRegistered: number;
  /** How many placeholder pens had to be created because the farm had no free location. */
  locationsCreated: number;
}

/** The provider the device was discovered through. Whysor is the only one so far. */
export type SensorDeviceProvider = 'Whysor';

/**
 * The provider type as the farm configuration endpoints spell it.
 *
 * Same string as `SensorDeviceProvider`, but they are not the same thing and are not guaranteed to stay
 * equal: this one keys `/farms/{id}/configuration-providers`, where `PigExpert` and `ePigs` also live.
 */
export const SENSOR_PROVIDER_TYPE = 'Whysor';

/**
 * Whether this farm can have sensor devices at all - read off its provider configuration rather than
 * inferred from an empty device list.
 *
 * The distinction matters because the two look identical in the table and need opposite things from the
 * reader: a farm with the provider enabled and no devices yet wants the discover button, and a farm with no
 * provider wants the External Providers panel. `discover` answers this too, as error 3005, but only after a
 * round trip to an external service that was never going to succeed.
 */
export interface SensorProviderStatus {
  /** False when the farm has no Whysor row at all - it has never been configured. */
  isConfigured: boolean;
  /** False when the row exists but is switched off. Discovery and the daily sync both do nothing. */
  isEnabled: boolean;
  /**
   * The customer's organization at the provider. Whysor keys its whole device list off this, so a blank one
   * is why discovery fails with 3006 even though the provider is enabled.
   */
  organizationName: string | null;
}

/**
 * The farm has no provider row at all.
 *
 * A finding, not a default: return it only when the configuration list came back and had no Whysor entry.
 * A caller that could not ask should represent that as its own absent state, so the UI never reports "not
 * set up" about a farm it failed to reach.
 */
export const UNCONFIGURED_SENSOR_PROVIDER: SensorProviderStatus = {
  isConfigured: false,
  isEnabled: false,
  organizationName: null
};

/**
 * The kind of thing the device is. Serialised as a string from a fixed set, so treat it as an enum.
 *
 * `WeatherStation` is the one that changes what the UI offers: it is an online weather service rather than
 * hardware in a barn, so it can never hold a location and the backend returns 400 if you try.
 */
export type SensorDeviceType = 'IndoorClimate' | 'WeatherStation' | 'Unknown';

/**
 * Why a device's pen is changing. The backend needs this and the two answers do opposite things to the
 * readings already stored, so it is asked as a question rather than defaulted silently.
 */
export type SensorDeviceLocationChangeReason = 'Correction' | 'Relocation';

/**
 * The farm has no enabled Whysor provider, so there is nothing to ask. Worth reacting to rather than only
 * reporting: the fix is one panel further up the same tab, and saying so is more use than the message alone.
 */
export const SENSOR_DEVICE_NO_PROVIDER_CODE = 'hs-sensor-devices-3005';

const deviceTypeLabelKeys: Record<SensorDeviceType, string> = {
  IndoorClimate: 'f-sensor-dev-type-indoor',
  WeatherStation: 'f-sensor-dev-type-weather',
  Unknown: 'f-sensor-dev-type-unknown'
};

/** Falls back to `Unknown` for a type this build has not heard of, rather than rendering the raw enum name. */
export const sensorDeviceTypeLabelKey = (deviceType: string): string =>
  deviceTypeLabelKeys[deviceType as SensorDeviceType] ?? deviceTypeLabelKeys.Unknown;

/**
 * Whether the device could **ever** hold a pen, by what kind of thing it is.
 *
 * Only `WeatherStation` is excluded, not everything that is not `IndoorClimate`. If a device of some type this
 * build does not recognise really cannot be bound, offering the action costs one 400 whose message we show;
 * hiding it would leave the device unassignable with nothing the farmer can do about it.
 */
export const canBindToLocation = (device: SensorDevice): boolean => device.deviceType !== 'WeatherStation';

/**
 * Whether binding it **now** would achieve anything.
 *
 * Separate from `canBindToLocation` because the two answers need different words in front of the reader: a
 * weather station is the wrong kind of thing, a retired device is the right kind of thing that the provider
 * has stopped reporting. Both mean "no action here", and neither is outstanding work someone can clear.
 */
export const isBindable = (device: SensorDevice): boolean => canBindToLocation(device) && !device.isRetired;

/**
 * Past this, the provider has stopped returning the device.
 *
 * Two missed daily syncs rather than one: `lastSeenAt` moves at most once a day even on a healthy device, so a
 * 24-hour threshold would flag half a working fleet.
 */
export const SENSOR_DEVICE_STALE_AFTER_HOURS = 48;

const HOUR_MS = 60 * 60 * 1000;

/**
 * Reported by the provider, but not recently - the "quiet but not retired" state.
 *
 * Deliberately not applied to a retired device. Since the backend started answering that outright, an old
 * `lastSeenAt` on a retired device is simply explained by the retirement, and a second marker saying so with
 * a different rule behind it would be noise. A device never seen at all is not stale either - it is new, and
 * its missing pen is the thing to look at.
 */
export const isSensorDeviceStale = (device: SensorDevice, now: number = Date.now()): boolean => {
  if (device.isRetired || !device.lastSeenAt) {
    return false;
  }

  return now - new Date(device.lastSeenAt).getTime() >= SENSOR_DEVICE_STALE_AFTER_HOURS * HOUR_MS;
};

/** How a bound pen reads in one line: "Room 3 / Pen 12". Falsy parts are dropped rather than left as gaps. */
export const formatBoundPen = (device: SensorDevice): string =>
  [device.locationRoom, device.locationPen].filter((part) => !!part).join(' / ');
